/**
 * Browser notification + audible alarm scheduler.
 * - Medication reminders play a real ringing sound + notification
 * - Positive affirmations delivered several times a day (mood-aware)
 * NOTE: While tab is open, reminders fire reliably. Background reminders when the
 * tab is closed require a Service Worker + Push server (future enhancement).
 */
import { MED_TIMES, AFFIRMATIONS } from './constants';

const MED_TIMERS_KEY = '__salaam_med_timers';
const AFF_TIMERS_KEY = '__salaam_aff_timers';

function clearTimers(key) {
  const timers = window[key] || [];
  timers.forEach(id => clearTimeout(id));
  window[key] = [];
}

function nextOccurrence(hourMinute) {
  const [h, m] = hourMinute.split(':').map(n => parseInt(n, 10));
  const now = new Date();
  const target = new Date();
  target.setHours(h, m, 0, 0);
  if (target.getTime() <= now.getTime()) target.setDate(target.getDate() + 1);
  return target;
}

export async function ensureNotificationsAllowed() {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  const perm = await Notification.requestPermission();
  return perm;
}

// ============= Audible ringing alarm (Web Audio API — no external files) =============
let sharedCtx = null;
function getAudioCtx() {
  try {
    if (!sharedCtx) sharedCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (sharedCtx.state === 'suspended') sharedCtx.resume();
    return sharedCtx;
  } catch (e) { return null; }
}

/**
 * Ring an alarm-like series of beeps (approx 3 seconds). Warm bell tone.
 */
export function playReminderBell() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  const beep = (t0, freq, dur, vol = 0.35) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, t0);
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(vol, t0 + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + dur + 0.05);
  };
  const now = ctx.currentTime;
  // Three ascending chimes (bell-like)
  beep(now + 0.00, 880, 0.45);
  beep(now + 0.10, 1174, 0.45);
  beep(now + 0.55, 880, 0.45);
  beep(now + 0.65, 1174, 0.45);
  beep(now + 1.10, 1318, 0.55, 0.4);
  beep(now + 1.20, 987, 0.55, 0.4);
}

// ============= Medication reminders =============
export function scheduleMedicationReminders(meds) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return 0;
  clearTimers(MED_TIMERS_KEY);
  const now = Date.now();
  const timers = [];
  const timeMap = Object.fromEntries(MED_TIMES.map(t => [t.code, t]));

  (meds || []).forEach(medRaw => {
    const med = typeof medRaw === 'string' ? { name: medRaw, schedule: ['morning'] } : medRaw;
    (med.schedule || []).forEach(code => {
      const t = timeMap[code];
      if (!t) return;
      const when = nextOccurrence(t.hour);
      const delay = when.getTime() - now;
      if (delay > 0 && delay < 24 * 60 * 60 * 1000) {
        const id = setTimeout(() => {
          try {
            playReminderBell();
            new Notification('حان وقت الدواء 💊', {
              body: `${med.name}${med.dose ? ' — ' + med.dose : ''} · ${t.label}`,
              icon: '/favicon.ico',
              tag: `med-${med.name}-${code}`,
              requireInteraction: true,
            });
          } catch (e) { /* ignore */ }
        }, delay);
        timers.push(id);
      }
    });
  });
  window[MED_TIMERS_KEY] = timers;
  return timers.length;
}

// ============= Positive affirmations (mood-aware) =============
// Delivers 4 uplifting notifications spread through the day.
// If latestMood is provided, biases the affirmation towards helpful for that state.
const AFF_TIMES = ['09:30', '13:00', '17:00', '20:30'];

function pickAffirmationForMood(latestMoodCode) {
  const list = AFFIRMATIONS || [];
  if (!list.length) return null;
  // Very simple bias: if mood is 'low', 'bad', 'anxious' choose ones with words
  const gentleWords = ['رفق', 'حنان', 'نفس', 'أمل', 'ضوء', 'سلام', 'دافئ'];
  const energyWords = ['قوة', 'صبر', 'بذور', 'خطوة', 'شجاع'];
  const target = ['low', 'bad', 'anxious'].includes(latestMoodCode) ? gentleWords
    : ['manic', 'irritable'].includes(latestMoodCode) ? gentleWords
    : energyWords;
  const scored = list.map(a => ({ a, s: target.reduce((n, w) => n + (a.text.includes(w) ? 1 : 0), 0) + Math.random() * 0.3 }));
  scored.sort((x, y) => y.s - x.s);
  return scored[0]?.a;
}

export function scheduleAffirmations(latestMoodCode) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return 0;
  clearTimers(AFF_TIMERS_KEY);
  const now = Date.now();
  const timers = [];
  AFF_TIMES.forEach(hm => {
    const when = nextOccurrence(hm);
    const delay = when.getTime() - now;
    if (delay > 0 && delay < 24 * 60 * 60 * 1000) {
      const id = setTimeout(() => {
        try {
          const aff = pickAffirmationForMood(latestMoodCode);
          if (aff) {
            new Notification('رسالة لكِ من سلام 🌿', {
              body: `«${aff.text}» — ${aff.author || ''}`,
              icon: '/favicon.ico',
              tag: `aff-${hm}`,
            });
          }
        } catch (e) { /* ignore */ }
      }, delay);
      timers.push(id);
    }
  });
  window[AFF_TIMERS_KEY] = timers;
  return timers.length;
}

/** Fire an immediate confirmation notification (used to test permissions). */
export function fireTestNotification() {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;
  playReminderBell();
  new Notification('سلام 🌿', { body: 'التذكيرات جاهزة لك — ستصلك في مواعيدها.', icon: '/favicon.ico' });
  return true;
}
